<template>
  <div v-if="hasMetadata" class="bookmark-metadata" :class="`metadata-${type.toLowerCase()}`">
    <!-- VIDEO metadata -->
    <div v-if="type === 'VIDEO'" class="metadata-items">
      <div v-if="metadata.views" class="metadata-item">
        <Eye :size="14" />
        <span>{{ formattedViews }}</span>
      </div>
      <div v-if="metadata.duration_seconds" class="metadata-item">
        <Clock :size="14" />
        <span>{{ formattedDuration }}</span>
      </div>
      <div v-if="metadata.rating" class="metadata-item">
        <ThumbsUp :size="14" />
        <span>{{ formattedRating }}</span>
      </div>
    </div>
    
    <!-- BOOK metadata -->
    <div v-else-if="type === 'BOOK'" class="metadata-items">
      <div v-if="metadata.authors && formatAuthors(metadata.authors)" class="metadata-item">
        <User :size="14" />
        <span>{{ formatAuthors(metadata.authors) }}</span>
      </div>
      <div v-if="metadata.publish_date && formatPublishDate(metadata.publish_date)" class="metadata-item">
        <Calendar :size="14" />
        <span>{{ formatPublishDate(metadata.publish_date) }}</span>
      </div>
      <div v-if="metadata.num_pages && metadata.num_pages.content" class="metadata-item">
        <BookOpen :size="14" />
        <span>{{ metadata.num_pages.content }} pages</span>
      </div>
      <div v-if="metadata.rating && formatBookRatingWithCount(metadata.rating, metadata.rating_count)" class="metadata-item rating">
        <Star :size="14" :fill="'#8030b7'" :color="'#8030b7'" />
        <span>{{ formatBookRatingWithCount(metadata.rating, metadata.rating_count) }}</span>
      </div>
    </div>
    
    <!-- RECIPE metadata -->
    <div v-else-if="type === 'RECIPE'" class="metadata-items">
      <div v-if="metadata.cooking_minutes && metadata.cooking_minutes.content > 0" class="metadata-item">
        <ChefHat :size="14" />
        <span>{{ formatRecipeTime(metadata.cooking_minutes.content) }} cook</span>
      </div>
      <div v-if="metadata.ready_in_minutes && metadata.ready_in_minutes.content > 0" class="metadata-item">
        <Clock :size="14" />
        <span>{{ formatRecipeTime(metadata.ready_in_minutes.content) }} total</span>
      </div>
      <div v-if="metadata.preparation_minutes && metadata.preparation_minutes.content > 0" class="metadata-item">
        <Timer :size="14" />
        <span>{{ formatRecipeTime(metadata.preparation_minutes.content) }} prep</span>
      </div>
      <div v-if="metadata.price_per_serving && metadata.price_per_serving.content > 0" class="metadata-item">
        <DollarSign :size="14" />
        <span>${{ formatPriceFromCents(metadata.price_per_serving.content) }}/serving</span>
      </div>
      <div v-if="metadata.dish_types && metadata.dish_types.content && metadata.dish_types.content.length > 0" class="metadata-item">
        <UtensilsCrossed :size="14" />
        <span>{{ formatArrayContent(metadata.dish_types.content) }}</span>
      </div>
      <div v-if="metadata.cuisines && metadata.cuisines.content && metadata.cuisines.content.length > 0" class="metadata-item">
        <Globe :size="14" />
        <span>{{ formatArrayContent(metadata.cuisines.content) }}</span>
      </div>
      
      <!-- Nutrition summary -->
      <div v-if="nutritionSummary" class="metadata-item">
        <ChartPie :size="14" />
        <span>{{ nutritionSummary }}</span>
      </div>
      
      <div v-if="metadata.rating_value && formatRecipeRatingWithCount(metadata.rating_value, metadata.rating_count)" class="metadata-item rating">
        <Star :size="14" :fill="'#f59e0b'" :color="'#f59e0b'" />
        <span>{{ formatRecipeRatingWithCount(metadata.rating_value, metadata.rating_count) }}</span>
      </div>
      
      <!-- Button to open full recipe details -->
      <Button 
        v-if="hasRecipeDetails"
        label="View Details" 
        icon="pi pi-book"
        class="p-button-sm recipe-details-button"
        @click.stop="showRecipeDetails = true"
      />
    </div>
    
    <!-- MOVIE metadata -->
    <div v-else-if="type === 'MOVIE'" class="metadata-items">
      <div v-if="metadata.release_date && formatMovieReleaseDate(metadata.release_date)" class="metadata-item">
        <Calendar :size="14" />
        <span>{{ formatMovieReleaseDate(metadata.release_date) }}</span>
      </div>
      <div v-if="metadata.duration_minutes && metadata.duration_minutes.content" class="metadata-item">
        <Clock :size="14" />
        <span>{{ metadata.duration_minutes.content }} min</span>
      </div>
      <div v-if="metadata.director && metadata.director.content" class="metadata-item">
        <User :size="14" />
        <span>{{ metadata.director.content }}</span>
      </div>
      <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="metadata-item">
        <Film :size="14" />
        <span>{{ formatArrayContent(metadata.genres.content) }}</span>
      </div>
      <div v-if="metadata.imdb_rating && metadata.imdb_rating.content" class="metadata-item imdb-rating">
        <img src="/imdb-logo-black.svg" alt="IMDb" class="imdb-logo" />
        <span><strong>{{ formatImdbRatingWithCount(metadata.imdb_rating, imdbRatingCount(metadata)) }}</strong></span>
      </div>
      <div v-else-if="metadata.rating && formatMovieRating(metadata.rating)" class="metadata-item movie-rating">
        <ThumbsUp :size="14" />
        <span>{{ formatMovieRating(metadata.rating) }}</span>
      </div>

      <!-- Streaming availability chip (lazy chunk; never mounts on public/embed renders) -->
      <StreamingAvailability
        v-if="showStreamingAvailability && linkId"
        :link-id="linkId"
        :metadata="metadata"
        @open-details="showMovieDetails = true"
      />

      <!-- Button to open movie details modal -->
      <Button
        v-if="hasMovieDetails"
        label="View Details"
        icon="pi pi-play"
        class="p-button-sm movie-details-button"
        @click.stop="showMovieDetails = true"
      />
    </div>
    
    <!-- TV_SHOW metadata -->
    <div v-else-if="type === 'TV_SHOW'" class="metadata-items">
      <div v-if="metadata.start_date && formatTvShowStartDate(metadata.start_date)" class="metadata-item">
        <Calendar :size="14" />
        <span>{{ formatTvShowStartDate(metadata.start_date) }}</span>
      </div>
      <div v-if="metadata.seasons && metadata.seasons.content" class="metadata-item">
        <Film :size="14" />
        <span>{{ metadata.seasons.content }} {{ metadata.seasons.content === 1 ? 'season' : 'seasons' }}</span>
      </div>
      <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="metadata-item">
        <Tv :size="14" />
        <span>{{ formatArrayContent(metadata.genres.content) }}</span>
      </div>
      <div v-if="metadata.imdb_rating && metadata.imdb_rating.content" class="metadata-item imdb-rating">
        <img src="/imdb-logo-black.svg" alt="IMDb" class="imdb-logo" />
        <span><strong>{{ formatImdbRatingWithCount(metadata.imdb_rating, imdbRatingCount(metadata)) }}</strong></span>
      </div>
      <div v-else-if="metadata.rating && formatTvShowRating(metadata.rating)" class="metadata-item tvshow-rating">
        <ThumbsUp :size="14" />
        <span>{{ formatTvShowRating(metadata.rating) }}</span>
      </div>

      <!-- Streaming availability chip (lazy chunk; never mounts on public/embed renders) -->
      <StreamingAvailability
        v-if="showStreamingAvailability && linkId"
        :link-id="linkId"
        :metadata="metadata"
        @open-details="showTvShowDetails = true"
      />

      <!-- Button to open TV show details modal -->
      <Button
        v-if="hasTvShowDetails"
        label="View Details"
        icon="pi pi-play"
        class="p-button-sm tvshow-details-button"
        @click.stop="showTvShowDetails = true"
      />
    </div>
    
    <!-- GAME metadata -->
    <div v-else-if="type === 'GAME'" class="metadata-items">
      <div v-if="metadata.release_date && formatGameReleaseDate(metadata.release_date)" class="metadata-item">
        <Calendar :size="14" />
        <span>{{ formatGameReleaseDate(metadata.release_date) }}</span>
      </div>
      <div v-if="metadata.median_playtime_hours && metadata.median_playtime_hours.content > 0" class="metadata-item">
        <Clock :size="14" />
        <span>{{ metadata.median_playtime_hours.content }}h playtime</span>
      </div>
      <div v-if="metadata.price && formatGamePrice(metadata.price, metadata.currency)" class="metadata-item">
        <DollarSign :size="14" />
        <span>{{ formatGamePrice(metadata.price, metadata.currency) }}</span>
      </div>
      <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="metadata-item">
        <Gamepad2 :size="14" />
        <span>{{ formatArrayContent(metadata.genres.content) }}</span>
      </div>
      <div v-if="hasGameBrainRating" class="metadata-item game-rating">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 40 40" fill="none" style="flex-shrink: 0;">
          <path d="M19.7275 32.2053C21.5954 31.1258 22.7437 29.1361 22.7437 26.9824V20.0609L26.8342 17.7008C26.9401 17.6373 27.0089 17.5208 27.0089 17.3991C27.0089 17.2774 26.9401 17.161 26.8342 17.0975L23.3258 15.0708C22.8972 14.8221 22.368 14.8221 21.9394 15.0708L9.70499 22.1352C9.27636 22.3839 9.01177 22.839 9.01177 23.3364V27.3899C9.01177 27.5169 9.08057 27.628 9.1864 27.6915C9.29223 27.755 9.42982 27.755 9.53565 27.6915L18.0183 22.7967V26.9559C18.0183 27.4375 17.759 27.8873 17.3409 28.1254L13.7373 30.2103L9.80024 32.4805C9.38219 32.7239 8.8689 32.7239 8.45085 32.4805L5.40812 30.7289C4.99008 30.4855 4.73078 30.041 4.73078 29.5595V10.4405C4.73078 9.95899 4.99008 9.50919 5.40812 9.27107L17.8278 2.10081C17.9336 2.03731 18.0024 1.92089 18.0024 1.79918C18.0024 1.67747 17.9336 1.56105 17.8278 1.49755L16.6424 0.809631C14.7744 -0.269877 12.4778 -0.269877 10.6152 0.809631L3.01627 5.19116C1.1483 6.27067 0 8.26035 0 10.4141V29.5806C0 31.7344 1.1483 33.724 3.01627 34.8035L6.10663 36.5868C7.9746 37.6664 10.2712 37.6664 12.1339 36.5868L19.7275 32.2053Z" fill="currentColor"/>
          <path d="M33.2424 34.8036C35.1104 33.7241 36.2587 31.7344 36.2587 29.5807V28.533C36.2587 28.0355 35.9941 27.5804 35.5655 27.3317L32.1312 25.3473C32.0783 25.3156 32.0412 25.2574 32.0412 25.1939C32.0412 25.1304 32.073 25.0722 32.1312 25.0404L33.2424 24.4001C35.1104 23.3206 36.2587 21.3309 36.2587 19.1772V15.6106C36.2587 13.4569 35.1051 11.4672 33.2424 10.393L25.6488 6.00616C23.7809 4.92666 21.4843 4.92666 19.6216 6.00616L9.70493 11.7318C9.2763 11.9805 9.01172 12.4356 9.01172 12.933V16.9865C9.01172 17.1135 9.08051 17.2246 9.18634 17.2881C9.29218 17.3516 9.42976 17.3516 9.5356 17.2881L22.2621 9.9379C22.4897 9.80561 22.7754 9.80561 23.0083 9.9379L31.1628 14.6422C31.3903 14.7745 31.5332 15.0179 31.5332 15.2878V19.5C31.5332 19.7646 31.3903 20.0133 31.1628 20.1456L27.7232 22.13C27.2946 22.3787 27.03 22.8338 27.03 23.3312V27.3794C27.03 27.5064 27.0988 27.6175 27.2046 27.681C27.3104 27.7445 27.448 27.7445 27.5539 27.681L31.2369 25.5537C31.3004 25.5167 31.3745 25.5167 31.438 25.5537C31.5015 25.5908 31.5385 25.6543 31.5385 25.7284V29.5437C31.5385 30.0252 31.2792 30.475 30.8612 30.7131L27.2522 32.7981L17.9071 38.1903L19.6216 39.1799C21.4896 40.2594 23.7862 40.2541 25.6488 39.1799L33.2424 34.793V34.8036Z" fill="currentColor"/>
        </svg>
        <span><strong>{{ formatGameRating(metadata.gamebrain_rating) }} ({{ formatGameReviewCount(metadata.gamebrain_rating_count) }})</strong></span>
      </div>
      <div v-else-if="metadata.rating" class="metadata-item game-rating">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 40 40" fill="none" style="flex-shrink: 0;">
          <path d="M19.7275 32.2053C21.5954 31.1258 22.7437 29.1361 22.7437 26.9824V20.0609L26.8342 17.7008C26.9401 17.6373 27.0089 17.5208 27.0089 17.3991C27.0089 17.2774 26.9401 17.161 26.8342 17.0975L23.3258 15.0708C22.8972 14.8221 22.368 14.8221 21.9394 15.0708L9.70499 22.1352C9.27636 22.3839 9.01177 22.839 9.01177 23.3364V27.3899C9.01177 27.5169 9.08057 27.628 9.1864 27.6915C9.29223 27.755 9.42982 27.755 9.53565 27.6915L18.0183 22.7967V26.9559C18.0183 27.4375 17.759 27.8873 17.3409 28.1254L13.7373 30.2103L9.80024 32.4805C9.38219 32.7239 8.8689 32.7239 8.45085 32.4805L5.40812 30.7289C4.99008 30.4855 4.73078 30.041 4.73078 29.5595V10.4405C4.73078 9.95899 4.99008 9.50919 5.40812 9.27107L17.8278 2.10081C17.9336 2.03731 18.0024 1.92089 18.0024 1.79918C18.0024 1.67747 17.9336 1.56105 17.8278 1.49755L16.6424 0.809631C14.7744 -0.269877 12.4778 -0.269877 10.6152 0.809631L3.01627 5.19116C1.1483 6.27067 0 8.26035 0 10.4141V29.5806C0 31.7344 1.1483 33.724 3.01627 34.8035L6.10663 36.5868C7.9746 37.6664 10.2712 37.6664 12.1339 36.5868L19.7275 32.2053Z" fill="currentColor"/>
          <path d="M33.2424 34.8036C35.1104 33.7241 36.2587 31.7344 36.2587 29.5807V28.533C36.2587 28.0355 35.9941 27.5804 35.5655 27.3317L32.1312 25.3473C32.0783 25.3156 32.0412 25.2574 32.0412 25.1939C32.0412 25.1304 32.073 25.0722 32.1312 25.0404L33.2424 24.4001C35.1104 23.3206 36.2587 21.3309 36.2587 19.1772V15.6106C36.2587 13.4569 35.1051 11.4672 33.2424 10.393L25.6488 6.00616C23.7809 4.92666 21.4843 4.92666 19.6216 6.00616L9.70493 11.7318C9.2763 11.9805 9.01172 12.4356 9.01172 12.933V16.9865C9.01172 17.1135 9.08051 17.2246 9.18634 17.2881C9.29218 17.3516 9.42976 17.3516 9.5356 17.2881L22.2621 9.9379C22.4897 9.80561 22.7754 9.80561 23.0083 9.9379L31.1628 14.6422C31.3903 14.7745 31.5332 15.0179 31.5332 15.2878V19.5C31.5332 19.7646 31.3903 20.0133 31.1628 20.1456L27.7232 22.13C27.2946 22.3787 27.03 22.8338 27.03 23.3312V27.3794C27.03 27.5064 27.0988 27.6175 27.2046 27.681C27.3104 27.7445 27.448 27.7445 27.5539 27.681L31.2369 25.5537C31.3004 25.5167 31.3745 25.5167 31.438 25.5537C31.5015 25.5908 31.5385 25.6543 31.5385 25.7284V29.5437C31.5385 30.0252 31.2792 30.475 30.8612 30.7131L27.2522 32.7981L17.9071 38.1903L19.6216 39.1799C21.4896 40.2594 23.7862 40.2541 25.6488 39.1799L33.2424 34.793V34.8036Z" fill="currentColor"/>
        </svg>
        <span>{{ formatGameRating(metadata.rating) }}</span>
      </div>
      
      <!-- Button to open game details modal -->
      <Button 
        v-if="hasGameDetails"
        label="View Details" 
        icon="pi pi-play"
        class="p-button-sm game-details-button"
        @click.stop="showGameDetails = true"
      />
    </div>
    
    <!-- PLACE metadata -->
    <div v-else-if="type === 'PLACE'" class="metadata-items">
      <div v-if="metadata.address && formatPlaceAddress(metadata.address)" class="metadata-item place-address">
        <MapPin :size="14" />
        <span>{{ formatPlaceAddress(metadata.address) }}</span>
      </div>
      <div v-if="metadata.rating && formatPlaceRatingWithCount(metadata.rating, metadata.rating_count)" class="metadata-item place-rating">
        <Star :size="14" :fill="'#ff69fc'" :color="'#ff69fc'" />
        <span>{{ formatPlaceRatingWithCount(metadata.rating, metadata.rating_count) }}</span>
      </div>
      
      <!-- Place type -->
      <div v-if="placeTypeLabel" class="metadata-item">
        <Hotel :size="14" />
        <span>{{ placeTypeLabel }}</span>
      </div>
      
      <!-- Button to open map modal -->
      <Button 
        v-if="metadata.location"
        label="Show Details" 
        icon="pi pi-map"
        class="p-button-sm place-details-button"
        @click.stop="showPlaceMap = true"
      />
    </div>
    
    <!-- PRODUCT metadata -->
    <div v-else-if="type === 'PRODUCT'" class="metadata-items">
      <div v-if="metadata.brand && metadata.brand.content" class="metadata-item">
        <Package :size="14" />
        <span>{{ metadata.brand.content }}</span>
      </div>
      <div v-if="metadata.price && formatProductPrice(metadata.price, metadata.currency)" class="metadata-item">
        <DollarSign :size="14" />
        <span>{{ formatProductPrice(metadata.price, metadata.currency) }}</span>
      </div>
      <div v-if="metadata.rating && formatProductRatingWithCount(metadata.rating, metadata.rating_count)" class="metadata-item rating">
        <Star :size="14" :fill="'#27ced5'" :color="'#27ced5'" />
        <span>{{ formatProductRatingWithCount(metadata.rating, metadata.rating_count) }}</span>
      </div>
    </div>
    
    <!-- ARTICLE metadata -->
    <div v-else-if="type === 'ARTICLE'" class="metadata-items">
      <div v-if="metadata.authors && formatArticleAuthors(metadata.authors)" class="metadata-item">
        <User :size="14" />
        <span>{{ formatArticleAuthors(metadata.authors) }}</span>
      </div>
      <div v-if="metadata.reading_time_minutes && metadata.reading_time_minutes.content" class="metadata-item">
        <Clock :size="14" />
        <span>{{ metadata.reading_time_minutes.content }} min read</span>
      </div>
      <div v-if="metadata.publish_date && formatArticlePublishDate(metadata.publish_date)" class="metadata-item">
        <Calendar :size="14" />
        <span>{{ formatArticlePublishDate(metadata.publish_date) }}</span>
      </div>
      <div v-if="(metadata.rating_value || metadata.rating) && formatArticleRatingWithCount(metadata.rating_value || metadata.rating, metadata.rating_count)" class="metadata-item article-rating">
        <ThumbsUp :size="14" />
        <span>{{ formatArticleRatingWithCount(metadata.rating_value || metadata.rating, metadata.rating_count) }}</span>
      </div>
    </div>
    
    <!-- IMAGE metadata -->
    <div v-else-if="file_type === 'IMAGE'" class="metadata-items">
      <div v-if="imageDescription" class="metadata-item image-description">
        <AlignLeft :size="14" />
        <span>{{ imageDescription }}</span>
      </div>
    </div>
    
    <!-- Custom/Generic metadata -->
    <!-- Removed to avoid duplication with LinkCard.vue -->
  </div>
  
  <!-- Recipe Details Modal -->
  <RecipeDetailsModal 
    v-if="type === 'RECIPE'"
    :visible="showRecipeDetails"
    :title="metadata.title || 'Recipe Details'"
    :metadata="metadata"
    @close="showRecipeDetails = false"
  />
  
  <!-- Place Map Modal -->
  <PlaceMapModal 
    v-if="type === 'PLACE'"
    :visible="showPlaceMap"
    :title="placeTitle"
    :location="metadata.location?.content"
    :address="formatPlaceAddressForModal(metadata.address)"
    :rating="metadata.rating?.content"
    :ratingCount="metadata.rating_count?.content"
    @close="showPlaceMap = false"
  />
  
  <!-- Movie Details Modal -->
  <MovieDetailsModal
    v-if="type === 'MOVIE'"
    :visible="showMovieDetails"
    :title="title || 'Movie Details'"
    :metadata="metadata"
    :link-id="linkId"
    :show-streaming="showStreamingAvailability && !!linkId"
    @close="showMovieDetails = false"
  />

  <!-- TV Show Details Modal -->
  <TvShowDetailsModal
    v-if="type === 'TV_SHOW'"
    :visible="showTvShowDetails"
    :title="title || 'TV Show Details'"
    :metadata="metadata"
    :link-id="linkId"
    :show-streaming="showStreamingAvailability && !!linkId"
    @close="showTvShowDetails = false"
  />
  
  <!-- Game Details Modal -->
  <GameDetailsModal 
    v-if="type === 'GAME'"
    :visible="showGameDetails"
    :title="title || 'Game Details'"
    :metadata="metadata"
    @close="showGameDetails = false"
  />
</template>

<script>
import { computed, ref, inject, defineAsyncComponent } from 'vue'
import { Eye, Clock, ThumbsUp, ChefHat, Timer, DollarSign, UtensilsCrossed, Globe, User, Calendar, BookOpen, Star, MapPin, Users, Package, Film, Gamepad2, Tv, ChartPie, Hotel, AlignLeft } from 'lucide-vue-next'
import { formatViews, formatDuration, formatRating, formatDate, formatDateLong, formatRecipeTime, formatAttributeName } from '../utils/metadataFormatters.js'
import Button from 'primevue/button'
import RecipeDetailsModal from './RecipeDetailsModal.vue'
import PlaceMapModal from './PlaceMapModal.vue'
import MovieDetailsModal from './MovieDetailsModal.vue'
import TvShowDetailsModal from './TvShowDetailsModal.vue'
import GameDetailsModal from './GameDetailsModal.vue'

// Deliberately a LAZY import: StreamingAvailability uses i18n, and this file is part of the
// i18n-free embed bundle closure (tests/embedNoI18n.test.js). The async chunk is only ever
// fetched when the chip actually renders — showStreamingAvailability stays false on
// public/embed renders, so the embed bundle never loads it.
const StreamingAvailability = defineAsyncComponent(() => import('./StreamingAvailability.vue'))

export default {
  name: 'BookmarkMetadata',
  components: {
    Eye,
    Clock,
    ThumbsUp,
    ChefHat,
    Timer,
    DollarSign,
    UtensilsCrossed,
    Globe,
    User,
    Calendar,
    BookOpen,
    Star,
    MapPin,
    Users,
    Package,
    Film,
    Gamepad2,
    Tv,
    ChartPie,
    Hotel,
    AlignLeft,
    Button,
    RecipeDetailsModal,
    PlaceMapModal,
    MovieDetailsModal,
    TvShowDetailsModal,
    GameDetailsModal,
    StreamingAvailability
  },
  props: {
    type: {
      type: String,
      default: 'GENERIC',
      validator: (value) => {
        return ['GENERIC', 'VIDEO', 'BOOK', 'RECIPE', 'MOVIE', 'TV_SHOW', 'GAME', 'PRODUCT', 'PLACE', 'ARTICLE', 'IMAGE'].includes(value)
      }
    },
    metadata: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: ''
    },
    linkId: {
      type: [Number, String],
      default: null
    },
    // Fail-closed: only the logged-in app passes true (never public/embed renders).
    showStreamingAvailability: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isPro = inject('isPro', ref(false))
    const showRecipeDetails = ref(false)
    const showPlaceMap = ref(false)
    const showMovieDetails = ref(false)
    const showTvShowDetails = ref(false)
    const showGameDetails = ref(false)
    
    const hasMetadata = computed(() => {
      if (!props.metadata) return false
      
      // Check if there's any meaningful metadata to display
      return Object.keys(props.metadata).length > 0
    })
    
    const formattedViews = computed(() => {
      if (!props.metadata.views?.content) return ''
      return formatViews(props.metadata.views.content)
    })
    
    const formattedDuration = computed(() => {
      if (!props.metadata.duration_seconds?.content) return ''
      return formatDuration(props.metadata.duration_seconds.content)
    })
    
    const formattedRating = computed(() => {
      if (!props.metadata.rating?.content) return ''
      return formatRating(props.metadata.rating.content)
    })
    
    const formatMetadataValue = (item) => {
      if (!item || typeof item !== 'object') return String(item || '')
      
      const { content, type } = item
      
      if (content === null || content === undefined) return ''
      
      // Normalize type to uppercase for comparison
      const normalizedType = type ? type.toUpperCase() : 'TEXTUAL'
      
      switch (normalizedType) {
        case 'DATE':
          // Format date using utility function
          return formatDate(content)
        case 'NUMERICAL':
          // Format number
          return typeof content === 'number' ? content.toLocaleString() : String(content)
        case 'LOCATION':
        case 'TEXTUAL':
        default:
          return String(content)
      }
    }
    
    // Recipe-specific helpers
    const formatPrice = (price) => {
      if (typeof price !== 'number') return price
      return price.toFixed(2)
    }
    
    const formatPriceFromCents = (cents) => {
      if (typeof cents !== 'number') return cents
      return (cents / 100).toFixed(2)
    }

    const formatRecipeRatingWithCount = (ratingData, countData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      let ratingStr = rating.toFixed(1)
      
      if (ratingStr.endsWith('.0')) {
        ratingStr = Math.round(rating).toString()
      }
      
      if (countData && typeof countData.content === 'number') {
        const count = countData.content
        const reviewText = count === 1 ? 'rating' : 'ratings'
        
        let formattedCount
        if (count >= 1000000) {
            formattedCount = `${(count / 1000000).toFixed(1)}M`
        } else if (count >= 1000) {
            formattedCount = `${(count / 1000).toFixed(1)}K` 
        } else {
            formattedCount = count.toString()
        }
        
        return `${ratingStr} (${formattedCount} ${reviewText})`
      }
      
      return ratingStr
    }
    
    const formatArrayContent = (arr) => {
      if (!Array.isArray(arr)) return ''
      // Capitalize first letter of each item and join with commas
      return arr.map(item => 
        String(item).charAt(0).toUpperCase() + String(item).slice(1)
      ).join(', ')
    }
    
    const hasRecipeDetails = computed(() => {
      if (props.type !== 'RECIPE') return false
      
      // Check if there are ingredients, instructions, or nutrition data
      return Boolean(
        (props.metadata.ingredients?.content && props.metadata.ingredients.content.length > 0) ||
        (props.metadata.instructions?.content && props.metadata.instructions.content.length > 0) ||
        props.metadata.nutrition?.content
      )
    })
    
    const nutritionSummary = computed(() => {
      if (props.type !== 'RECIPE') return ''
      
      const nutrition = props.metadata.nutrition?.content
      if (!nutrition || !nutrition.nutrients) return ''
      
      // Find the nutrition values
      const calories = nutrition.nutrients.find(n => n.name === 'Calories')
      const fat = nutrition.nutrients.find(n => n.name === 'Fat')
      const carbs = nutrition.nutrients.find(n => n.name === 'Carbohydrates')
      const protein = nutrition.nutrients.find(n => n.name === 'Protein')
      
      // Check if we have at least calories
      if (!calories) return ''
      
      // Build the summary string
      const parts = []
      
      // Add calories (rounded, no decimals)
      parts.push(`${Math.round(calories.amount)}cals`)
      
      // Add fat (rounded, no decimals)
      if (fat) {
        parts.push(`${Math.round(fat.amount)}g fat`)
      }
      
      // Add carbs (rounded, no decimals)
      if (carbs) {
        parts.push(`${Math.round(carbs.amount)}g carbs`)
      }
      
      // Add protein (rounded, no decimals)
      if (protein) {
        parts.push(`${Math.round(protein.amount)}g protein`)
      }
      
      return parts.join(', ')
    })
    
    // Book-specific helpers
    const formatAuthors = (authorsData) => {
      if (!authorsData || !authorsData.content) return ''
      
      const authors = authorsData.content
      
      // Handle if it's already a string
      if (typeof authors === 'string') {
        return authors
      }
      
      // Handle if it's an array
      if (Array.isArray(authors)) {
        if (authors.length === 0) return ''
        if (authors.length === 1) return authors[0]
        if (authors.length === 2) return `${authors[0]} & ${authors[1]}`
        return `${authors[0]} et al.`
      }
      
      return String(authors)
    }
    
    const formatPublishDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      const year = dateData.content
      
      // If it's just a year number
      if (typeof year === 'number') {
        return String(year)
      }
      
      // If it's a date string, extract the year
      if (typeof year === 'string') {
        const yearMatch = year.match(/\d{4}/)
        return yearMatch ? yearMatch[0] : year
      }
      
      return String(year)
    }
    
    const formatBookRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is between 0 and 1 (decimal), convert to 5-star scale
      if (rating >= 0 && rating <= 1) {
        const stars = (rating * 5).toFixed(1)
        return `${stars}`
      }
      
      // If rating is already on a scale (e.g., 1-5, 1-10)
      if (rating >= 1 && rating <= 5) {
        return `${rating.toFixed(1)}`
      }
      
      if (rating > 5 && rating <= 10) {
        return `${rating.toFixed(1)}/10`
      }
      
      return rating.toFixed(1)
    }
    
    const formatBookRatingWithCount = (ratingData, countData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      let ratingStr = ''
      
      // If rating is between 0 and 1 (decimal), convert to 5-star scale
      if (rating >= 0 && rating <= 1) {
        const stars = (rating * 5).toFixed(1)
        ratingStr = stars
      } else if (rating >= 1 && rating <= 5) {
        // If rating is already on a scale (e.g., 1-5)
        ratingStr = rating.toFixed(1)
      } else if (rating > 5 && rating <= 10) {
        ratingStr = `${rating.toFixed(1)}/10`
      } else {
        ratingStr = rating.toFixed(1)
      }
      
      // Add review count if available
      if (countData && typeof countData.content === 'number') {
        const count = countData.content
        const reviewText = count === 1 ? 'review' : 'reviews'
        
        // Format count: shorten if > 1000
        let formattedCount
        if (count >= 1000000) {
          formattedCount = `${(count / 1000000).toFixed(1)}M`
        } else if (count >= 1000) {
          formattedCount = `${(count / 1000).toFixed(1)}K`
        } else {
          formattedCount = count.toString()
        }
        
        ratingStr += ` (${formattedCount} ${reviewText})`
      }
      
      return ratingStr
    }
    
    // Place-specific helpers
    const placeTitle = computed(() => {
      // Use the bookmark's title if available
      if (props.title) {
        return props.title
      }
      
      // Fallback to metadata name
      if (props.metadata.name && props.metadata.name.content) {
        return props.metadata.name.content
      }
      
      // Fallback to address
      if (props.metadata.address) {
        const address = formatPlaceAddressForModal(props.metadata.address)
        return address || 'Location'
      }
      
      return 'Location'
    })
    
    const formatPlaceAddress = (addressData) => {
      if (!addressData || !addressData.content) return ''
      
      const address = addressData.content
      
      // If it's a string
      if (typeof address === 'string') {
        return address
      }
      
      // If it's an object with fullAddress
      if (typeof address === 'object' && address !== null) {
        if (address.fullAddress) {
          // Truncate if too long for display
          const maxLen = 60
          return address.fullAddress.length > maxLen 
            ? address.fullAddress.substring(0, maxLen) + '...'
            : address.fullAddress
        }
        
        // Construct short version from parts
        const parts = []
        if (address.streetAddress) parts.push(address.streetAddress)
        if (address.addressLocality) parts.push(address.addressLocality)
        if (address.addressRegion) parts.push(address.addressRegion)
        
        return parts.join(', ') || 'Address available'
      }
      
      return ''
    }
    
    const formatPlaceAddressForModal = (addressData) => {
      if (!addressData || !addressData.content) return null
      
      const address = addressData.content
      
      // Return as-is for the modal (it will handle formatting)
      if (typeof address === 'string') {
        return address
      }
      
      if (typeof address === 'object' && address !== null) {
        return address
      }
      
      return null
    }
    
    const formatPlaceRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      // Otherwise show as rating out of 5
      return `${rating.toFixed(1)}/5`
    }
    
    const formatPlaceReviewCount = (countData) => {
      if (!countData || typeof countData.content !== 'number') return ''
      
      const count = countData.content
      
      if (count === 1) {
        return '1 review'
      }
      
      return `${count.toLocaleString()} reviews`
    }
    
    const formatPlaceRatingWithCount = (ratingData, countData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      let ratingStr = ''
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        ratingStr = `${Math.round(rating * 100)}%`
      } else {
        // Otherwise show as rating without /5
        // Remove .0 decimal but keep other decimals like .1, .5, etc.
        const ratingValue = rating.toFixed(1)
        ratingStr = ratingValue.endsWith('.0') ? Math.round(rating).toString() : ratingValue
      }
      
      // Add review count if available
      if (countData && typeof countData.content === 'number') {
        const count = countData.content
        const reviewText = count === 1 ? 'review' : 'reviews'
        ratingStr += ` (${count.toLocaleString()} ${reviewText})`
      }
      
      return ratingStr
    }
    
    // Product-specific helpers
    const formatProductPrice = (priceData, currencyData) => {
      if (!priceData || typeof priceData.content !== 'number') return ''
      
      const price = priceData.content
      const currency = currencyData?.content || 'USD'
      
      return `${currency} ${price.toFixed(2)}`
    }
    
    const formatProductRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      // Otherwise show as decimal rating
      return rating.toFixed(1)
    }
    
    const formatProductRatingWithCount = (ratingData, countData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      let ratingStr = ''
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        ratingStr = `${Math.round(rating * 100)}%`
      } else {
        // Otherwise show as decimal rating
        ratingStr = rating.toFixed(1)
      }
      
      // Add review count if available
      if (countData && typeof countData.content === 'number') {
        const count = countData.content
        const reviewText = count === 1 ? 'review' : 'reviews'
        
        // Format count: shorten if > 1000
        let formattedCount
        if (count >= 1000000) {
          formattedCount = `${(count / 1000000).toFixed(1)}M`
        } else if (count >= 1000) {
          formattedCount = `${(count / 1000).toFixed(1)}K`
        } else {
          formattedCount = count.toString()
        }
        
        ratingStr += ` (${formattedCount} ${reviewText})`
      }
      
      return ratingStr
    }
    
    const formatProductReviewCount = (countData) => {
      if (!countData || typeof countData.content !== 'number') return ''
      
      const count = countData.content
      
      if (count === 1) {
        return '1 review'
      }
      
      return `${count.toLocaleString()} reviews`
    }
    
    // Movie-specific helpers
    const formatMovieReleaseDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      const dateStr = dateData.content
      
      return formatDate(dateStr)
    }
    
    const formatMovieRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }

    /**
     * Vote count to show beside the IMDb rating. `rating_count` is only IMDb's when the rating
     * itself came off an imdb.com page; on a JustWatch/Letterboxd save it is that site's own count
     * (JustWatch: 4,880 users vs IMDb's 91,768), so `imdb_votes` — set by the backend whenever the
     * IMDb rating is read from a third-party page — takes precedence.
     */
    const imdbRatingCount = (metadata) => metadata?.imdb_votes ?? metadata?.rating_count

    const formatImdbRatingWithCount = (ratingData, countData) => {
      if (!ratingData || ratingData.content === undefined || ratingData.content === null) return ''

      let ratingValue = ratingData.content

      if (typeof ratingValue === 'string') {
        const parsedRating = parseFloat(ratingValue)
        if (!Number.isNaN(parsedRating)) {
          ratingValue = parsedRating
        } else {
          return ratingValue
        }
      }

      if (typeof ratingValue !== 'number' || Number.isNaN(ratingValue)) {
        return String(ratingValue)
      }

      let ratingStr = ratingValue.toFixed(1)
      if (ratingStr.endsWith('.0')) {
        ratingStr = ratingStr.slice(0, -2)
      }

      const countRaw = countData?.content
      if (countRaw === undefined || countRaw === null) {
        return ratingStr
      }

      let countValue = countRaw

      if (typeof countValue === 'string') {
        const numericString = countValue.replace(/[^0-9.]/g, '')
        const parsedCount = parseInt(numericString, 10)
        if (!Number.isNaN(parsedCount)) {
          countValue = parsedCount
        } else {
          return ratingStr
        }
      }

      if (typeof countValue !== 'number' || !Number.isFinite(countValue) || countValue <= 0) {
        return ratingStr
      }

      const formatSuffixNumber = (value, divisor, suffix) => {
        const formatted = (value / divisor).toFixed(1)
        return formatted.endsWith('.0') ? `${formatted.slice(0, -2)}${suffix}` : `${formatted}${suffix}`
      }

      let formattedCount
      if (countValue >= 1000000) {
        formattedCount = formatSuffixNumber(countValue, 1000000, 'M')
      } else if (countValue >= 1000) {
        formattedCount = formatSuffixNumber(countValue, 1000, 'K')
      } else {
        formattedCount = countValue.toLocaleString()
      }

      const ratingsLabel = countValue === 1 ? 'rating' : 'ratings'

      return `${ratingStr} (${formattedCount} ${ratingsLabel})`
    }
    
    const hasMovieDetails = computed(() => {
      if (props.type !== 'MOVIE') return false
      
      // Check if there's a trailer or actors list
      return Boolean(
        props.metadata.trailer?.content ||
        (props.metadata.actors?.content && props.metadata.actors.content.length > 0)
      )
    })
    
    // TV Show-specific helpers
    const formatTvShowStartDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      const dateStr = dateData.content
      
      return formatDate(dateStr)
    }
    
    const formatTvShowRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // If rating is < 1, show as percentage
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }
    
    const hasTvShowDetails = computed(() => {
      if (props.type !== 'TV_SHOW') return false
      
      // Check if there's a trailer
      return Boolean(props.metadata.trailer?.content)
    })
    
    // Game-specific helpers
    const formatGameReleaseDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      const dateStr = dateData.content
      
      return formatDate(dateStr)
    }
    
    const formatGamePrice = (priceData, currencyData) => {
      if (!priceData || typeof priceData.content !== 'number') return ''
      
      const price = priceData.content
      const currency = currencyData?.content || 'USD'
      
      return `${currency} ${price.toFixed(2)}`
    }
    
    const formatGameRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // Always show as percentage (rating is < 1)
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }
    
    const formatGameReviewCount = (countData) => {
      if (!countData || typeof countData.content !== 'number') return ''
      
      const count = countData.content
      
      if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1)}M`
      }
      
      if (count >= 1000) {
        return `${(count / 1000).toFixed(1)}K`
      }
      
      return count.toLocaleString()
    }
    
    const hasGameBrainRating = computed(() => {
      return Boolean(
        props.metadata.gamebrain_rating?.content &&
        props.metadata.gamebrain_rating_count?.content
      )
    })
    
    const hasGameDetails = computed(() => {
      if (props.type !== 'GAME') return false
      
      // Check if there's gameplay video or gamebrain URL
      return Boolean(
        props.metadata.gameplay_video?.content ||
        props.metadata.gamebrain_url?.content
      )
    })
    
    // Article-specific helpers
    const formatArticleAuthors = (authorsData) => {
      if (!authorsData || !authorsData.content) return ''
      
      const authors = authorsData.content
      
      // Handle if it's already a string
      if (typeof authors === 'string') {
        return authors
      }
      
      // Handle if it's an array
      if (Array.isArray(authors)) {
        if (authors.length === 0) return ''
        if (authors.length === 1) return authors[0]
        if (authors.length === 2) return `${authors[0]} & ${authors[1]}`
        return `${authors[0]} et al.`
      }
      
      return String(authors)
    }
    
    const formatArticlePublishDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      const dateStr = dateData.content
      
      return formatDate(dateStr)
    }

    const formatArticleRatingWithCount = (ratingData, countData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      let ratingStr = ''
      
      // Handle different rating scales
      if (rating >= 0 && rating <= 1) {
        // Percentage-based (e.g. 0.85 → 85%)
        ratingStr = `${Math.round(rating * 100)}%`
      } else if (rating > 1 && rating <= 5) {
        ratingStr = rating.toFixed(1)
        if (ratingStr.endsWith('.0')) ratingStr = Math.round(rating).toString()
      } else if (rating > 5 && rating <= 10) {
        ratingStr = `${rating.toFixed(1)}/10`
      } else if (rating > 10 && rating <= 100) {
        ratingStr = `${Math.round(rating)}%`
      } else {
        ratingStr = rating.toFixed(1)
      }
      
      // Add review count if available
      if (countData && typeof countData.content === 'number') {
        const count = countData.content
        const reviewText = count === 1 ? 'rating' : 'ratings'
        
        let formattedCount
        if (count >= 1000000) {
          formattedCount = `${(count / 1000000).toFixed(1)}M`
        } else if (count >= 1000) {
          formattedCount = `${(count / 1000).toFixed(1)}K`
        } else {
          formattedCount = count.toString()
        }
        
        ratingStr += ` (${formattedCount} ${reviewText})`
      }
      
      return ratingStr
    }
    
    // Place type label helper
    const placeTypeLabel = computed(() => {
      if (props.type !== 'PLACE') return null
      
      // Check for various possible field names for place type
      const placeType = props.metadata.place_type?.content || 
                       props.metadata.placeType?.content || 
                       props.metadata.category?.content ||
                       props.metadata.type?.content
      
      if (!placeType) return null
      
      // Capitalize first letter
      return String(placeType).charAt(0).toUpperCase() + String(placeType).slice(1)
    })
    
    // IMAGE description from automatic metadata
    const imageDescription = computed(() => {
      if (!props.metadata.description) return ''
      const desc = props.metadata.description
      // Handle both object format { content: "..." } and plain string
      if (typeof desc === 'object' && desc.content) {
        return String(desc.content)
      }
      if (typeof desc === 'string') {
        return desc
      }
      return ''
    })

    return {
      hasMetadata,
      formattedViews,
      formattedDuration,
      formatRecipeTime,
      formattedRating,
      formatAttributeName,
      formatMetadataValue,
      formatPrice,
      formatPriceFromCents,
      formatRecipeRatingWithCount,
      formatArrayContent,
      hasRecipeDetails,
      nutritionSummary,
      showRecipeDetails,
      formatAuthors,
      formatPublishDate,
      formatBookRating,
      formatBookRatingWithCount,
      showPlaceMap,
      placeTitle,
      formatPlaceAddress,
      formatPlaceAddressForModal,
      formatPlaceRating,
      formatPlaceReviewCount,
      formatPlaceRatingWithCount,
      formatProductPrice,
      formatProductRating,
      formatProductRatingWithCount,
      formatProductReviewCount,
      formatMovieReleaseDate,
      formatMovieRating,
      formatImdbRatingWithCount,
      imdbRatingCount,
      hasMovieDetails,
      showMovieDetails,
      formatTvShowStartDate,
      formatTvShowRating,
      hasTvShowDetails,
      showTvShowDetails,
      formatGameReleaseDate,
      formatGamePrice,
      formatGameRating,
      formatGameReviewCount,
      hasGameBrainRating,
      hasGameDetails,
      showGameDetails,
      formatArticleAuthors,
      formatArticlePublishDate,
      formatArticleRatingWithCount,
      placeTypeLabel,
      imageDescription
    }
  }
}
</script>

<style scoped>
.bookmark-metadata {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.metadata-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.metadata-item {
  display: flex;
  align-items: anchor-center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  background: var(--bg-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: background 0.2s ease;
  min-width: 0;
  max-width: 100%;
}

.metadata-item:hover {
  background: var(--bg-tertiary, var(--bg-primary));
}

.metadata-item svg {
  flex-shrink: 0;
}

.metadata-item .imdb-logo {
  height: 14px;
  width: auto;
  flex-shrink: 0;
}

.metadata-item span {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
}

/* Custom metadata styling */
.custom-metadata {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.metadata-item.custom {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.5rem;
}

.metadata-item.custom .metadata-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metadata-item.custom .metadata-value {
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-word;
  white-space: normal;
}

/* Video-specific styling */
.metadata-video .metadata-item {
  border: 1px solid rgba(255, 0, 48, 0.1);
}

.metadata-video .metadata-item svg {
  color: #ff0030;
}

/* Book-specific styling */
.metadata-book .metadata-item {
  border: 1px solid rgba(128, 48, 183, 0.15);
  background: rgba(128, 48, 183, 0.03);
}

.metadata-book .metadata-item svg {
  color: #8030b7;
}

.metadata-book .metadata-item.rating {
  background: linear-gradient(135deg, rgba(128, 48, 183, 0.08) 0%, rgba(128, 48, 183, 0.03) 100%);
  border-color: rgba(128, 48, 183, 0.25);
  font-weight: 600;
}

.metadata-book .metadata-item.rating span {
  color: #8030b7;
}

/* Recipe-specific styling */
.metadata-recipe .metadata-item {
  border: 1px solid rgba(0, 132, 60, 0.1);
}

.metadata-recipe .metadata-item svg {
  color: #00843c;
}

.recipe-details-button {
  margin-top: 0.5rem;
  width: 100%;
  background: linear-gradient(135deg, #00843c 0%, #006d32 100%) !important;
  border: none !important;
  font-weight: 600;
}

.recipe-details-button:hover {
  background: linear-gradient(135deg, #006d32 0%, #005c2a 100%) !important;
  box-shadow: 0 4px 12px rgba(0, 132, 60, 0.3);
}

/* Place-specific styling */
.metadata-place .metadata-item {
  border: 1px solid rgba(255, 105, 252, 0.15);
  background: rgba(255, 105, 252, 0.03);
}

.metadata-place .metadata-item svg {
  color: #ff69fc;
}

.metadata-place .metadata-item.place-address {
  flex: 1 1 100%;
  max-width: 100%;
}

.metadata-place .metadata-item.place-address span {
  white-space: normal;
  word-break: break-word;
}

.metadata-place .metadata-item.place-rating {
  background: linear-gradient(135deg, rgba(255, 105, 252, 0.08) 0%, rgba(255, 105, 252, 0.03) 100%);
  border-color: rgba(255, 105, 252, 0.25);
  font-weight: 600;
}

.metadata-place .metadata-item.place-rating span {
  color: #ff69fc;
}

.place-details-button {
  margin-top: 0.5rem;
  width: 100%;
  background: linear-gradient(135deg, #ff69fc 0%, #e854e9 100%) !important;
  border: none !important;
  font-weight: 600;
}

.place-details-button:hover {
  background: linear-gradient(135deg, #e854e9 0%, #d440d6 100%) !important;
  box-shadow: 0 4px 12px rgba(255, 105, 252, 0.4);
}

/* Product-specific styling */
.metadata-product .metadata-item {
  border: 1px solid rgba(39, 206, 213, 0.15);
  background: rgba(39, 206, 213, 0.03);
}

.metadata-product .metadata-item svg {
  color: #27ced5;
}

.metadata-product .metadata-item.rating {
  background: linear-gradient(135deg, rgba(39, 206, 213, 0.08) 0%, rgba(39, 206, 213, 0.03) 100%);
  border-color: rgba(39, 206, 213, 0.25);
  font-weight: 600;
}

.metadata-product .metadata-item.rating span {
  color: #27ced5;
}

/* Article-specific styling */
.metadata-article .metadata-item {
  border: 1px solid rgba(194, 208, 255, 0.3);
  background: rgba(194, 208, 255, 0.05);
}

.metadata-article .metadata-item svg {
  color: #7790ff;
}

.metadata-article .metadata-item.article-rating {
  background: linear-gradient(135deg, rgba(119, 144, 255, 0.08) 0%, rgba(119, 144, 255, 0.03) 100%);
  border-color: rgba(119, 144, 255, 0.25);
  font-weight: 600;
}

.metadata-article .metadata-item.article-rating span {
  color: #7790ff;
}

/* IMAGE-specific styling */
.metadata-image .metadata-item {
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.05);
}

.metadata-image .metadata-item svg {
  color: #64748b;
}

.metadata-image .metadata-item.image-description {
  flex-basis: 100%;
  font-size: 0.8125rem;
  line-height: 1.4;
}

/* Movie-specific styling */
.metadata-movie .metadata-item {
  border: 1px solid rgba(205, 168, 1, 0.15);
  background: rgba(205, 168, 1, 0.03);
}

.metadata-movie .metadata-item svg {
  color: #cda801;
}

.metadata-movie .metadata-item.movie-rating {
  background: linear-gradient(135deg, rgba(205, 168, 1, 0.08) 0%, rgba(205, 168, 1, 0.03) 100%);
  border-color: rgba(205, 168, 1, 0.25);
  font-weight: 600;
}

.metadata-movie .metadata-item.movie-rating span {
  color: #cda801;
}

.metadata-movie .metadata-item.imdb-rating span {
  color: #cda801;
}

.metadata-movie .metadata-item.imdb-rating strong {
  font-weight: 700;
}

.movie-details-button {
  margin-top: 0.5rem;
  width: 100%;
  background: linear-gradient(135deg, #cda801 0%, #b89401 100%) !important;
  border: none !important;
  font-weight: 600;
}

.movie-details-button:hover {
  background: linear-gradient(135deg, #b89401 0%, #a48001 100%) !important;
  box-shadow: 0 4px 12px rgba(205, 168, 1, 0.4);
}

/* TV Show-specific styling */
.metadata-tv_show .metadata-item {
  border: 1px solid rgba(231, 116, 3, 0.15);
  background: rgba(231, 116, 3, 0.03);
}

.metadata-tv_show .metadata-item svg {
  color: #e77403;
}

.metadata-tv_show .metadata-item.tvshow-rating {
  background: linear-gradient(135deg, rgba(231, 116, 3, 0.08) 0%, rgba(231, 116, 3, 0.03) 100%);
  border-color: rgba(231, 116, 3, 0.25);
  font-weight: 600;
}

.metadata-tv_show .metadata-item.tvshow-rating span {
  color: #e77403;
}

.metadata-tv_show .metadata-item.imdb-rating span {
  color: #e77403;
}

.metadata-tv_show .metadata-item.imdb-rating strong {
  font-weight: 700;
}

.tvshow-details-button {
  margin-top: 0.5rem;
  width: 100%;
  background: linear-gradient(135deg, #e77403 0%, #d16603 100%) !important;
  border: none !important;
  font-weight: 600;
}

.tvshow-details-button:hover {
  background: linear-gradient(135deg, #d16603 0%, #bb5903 100%) !important;
  box-shadow: 0 4px 12px rgba(231, 116, 3, 0.4);
}

/* Game-specific styling */
.metadata-game .metadata-item {
  border: 1px solid rgba(51, 51, 51, 0.15);
  background: rgba(51, 51, 51, 0.03);
}

.metadata-game .metadata-item svg {
  color: #333333;
}

.metadata-game .metadata-item.game-rating {
  background: linear-gradient(135deg, rgba(51, 51, 51, 0.08) 0%, rgba(51, 51, 51, 0.03) 100%);
  border-color: rgba(51, 51, 51, 0.25);
}

.game-details-button {
  margin-top: 0.5rem;
  width: 100%;
  background: linear-gradient(135deg, #333333 0%, #1a1a1a 100%) !important;
  border: none !important;
  font-weight: 600;
}

.game-details-button:hover {
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%) !important;
  box-shadow: 0 4px 12px rgba(51, 51, 51, 0.4);
}

/* Dark theme: per-type BRAND re-tints only — the light-tuned brand colors
   are low-contrast on dark (token-based surfaces flip on their own). */
:root.dark-theme .metadata-video .metadata-item {
  border-color: rgba(255, 0, 48, 0.2);
}

:root.dark-theme .metadata-book .metadata-item {
  border-color: rgba(128, 48, 183, 0.3);
  background: rgba(128, 48, 183, 0.08);
}

:root.dark-theme .metadata-book .metadata-item svg {
  color: #b47fd9;
}

:root.dark-theme .metadata-book .metadata-item.rating {
  background: linear-gradient(135deg, rgba(128, 48, 183, 0.15) 0%, rgba(128, 48, 183, 0.08) 100%);
  border-color: rgba(128, 48, 183, 0.4);
}

:root.dark-theme .metadata-book .metadata-item.rating span {
  color: #b47fd9;
}

:root.dark-theme .metadata-recipe .metadata-item {
  border-color: rgba(0, 132, 60, 0.2);
}

:root.dark-theme .metadata-place .metadata-item {
  border-color: rgba(255, 105, 252, 0.3);
  background: rgba(255, 105, 252, 0.08);
}

:root.dark-theme .metadata-place .metadata-item svg {
  color: #ffb3fd;
}

:root.dark-theme .metadata-place .metadata-item.place-rating {
  background: linear-gradient(135deg, rgba(255, 105, 252, 0.15) 0%, rgba(255, 105, 252, 0.08) 100%);
  border-color: rgba(255, 105, 252, 0.4);
}

:root.dark-theme .metadata-place .metadata-item.place-rating span {
  color: #ffb3fd;
}

:root.dark-theme .metadata-product .metadata-item {
  border-color: rgba(39, 206, 213, 0.3);
  background: rgba(39, 206, 213, 0.08);
}

:root.dark-theme .metadata-product .metadata-item svg {
  color: #5ae5ec;
}

:root.dark-theme .metadata-product .metadata-item.rating {
  background: linear-gradient(135deg, rgba(39, 206, 213, 0.15) 0%, rgba(39, 206, 213, 0.08) 100%);
  border-color: rgba(39, 206, 213, 0.4);
}

:root.dark-theme .metadata-product .metadata-item.rating span {
  color: #5ae5ec;
}

:root.dark-theme .metadata-article .metadata-item {
  border-color: rgba(194, 208, 255, 0.3);
  background: rgba(194, 208, 255, 0.08);
}

:root.dark-theme .metadata-article .metadata-item svg {
  color: #a6b8ff;
}

:root.dark-theme .metadata-article .metadata-item.article-rating {
  background: linear-gradient(135deg, rgba(119, 144, 255, 0.15) 0%, rgba(119, 144, 255, 0.08) 100%);
  border-color: rgba(119, 144, 255, 0.4);
}

:root.dark-theme .metadata-article .metadata-item.article-rating span {
  color: #a6b8ff;
}

:root.dark-theme .metadata-image .metadata-item {
  border-color: rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.08);
}

:root.dark-theme .metadata-image .metadata-item svg {
  color: #94a3b8;
}

:root.dark-theme .metadata-movie .metadata-item {
  border-color: rgba(205, 168, 1, 0.3);
  background: rgba(205, 168, 1, 0.08);
}

:root.dark-theme .metadata-movie .metadata-item svg {
  color: #e8c647;
}

:root.dark-theme .metadata-movie .metadata-item.movie-rating {
  background: linear-gradient(135deg, rgba(205, 168, 1, 0.15) 0%, rgba(205, 168, 1, 0.08) 100%);
  border-color: rgba(205, 168, 1, 0.4);
}

:root.dark-theme .metadata-movie .metadata-item.movie-rating span {
  color: #e8c647;
}

:root.dark-theme .metadata-movie .metadata-item.imdb-rating span {
  color: #e8c647;
}

:root.dark-theme .metadata-tv_show .metadata-item {
  border-color: rgba(231, 116, 3, 0.3);
  background: rgba(231, 116, 3, 0.08);
}

:root.dark-theme .metadata-tv_show .metadata-item svg {
  color: #ff9347;
}

:root.dark-theme .metadata-tv_show .metadata-item.tvshow-rating {
  background: linear-gradient(135deg, rgba(231, 116, 3, 0.15) 0%, rgba(231, 116, 3, 0.08) 100%);
  border-color: rgba(231, 116, 3, 0.4);
}

:root.dark-theme .metadata-tv_show .metadata-item.tvshow-rating span {
  color: #ff9347;
}

:root.dark-theme .metadata-tv_show .metadata-item.imdb-rating span {
  color: #ff9347;
}

:root.dark-theme .metadata-game .metadata-item {
  border-color: rgba(153, 153, 153, 0.3);
  background: rgba(153, 153, 153, 0.08);
}

:root.dark-theme .metadata-game .metadata-item svg {
  color: #999999;
}

:root.dark-theme .metadata-game .metadata-item.game-rating {
  background: linear-gradient(135deg, rgba(153, 153, 153, 0.15) 0%, rgba(153, 153, 153, 0.08) 100%);
  border-color: rgba(153, 153, 153, 0.4);
}

/* The IMDb wordmark is a pure-black <img> asset no color rule can reach —
   invert it to white on dark. */
:root.dark-theme .metadata-item .imdb-logo {
  filter: invert(1);
}
</style>
